import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Building2,
  Cloud,
  Server,
  Play,
  Loader2,
  Download,
  TrendingUp,
  Shield,
  Zap,
  DollarSign,
  CheckCircle2,
  AlertTriangle,
  Database,
  Network,
  Lock,
  Sparkles,
  FileText,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArchitectureAdvisor = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [customerPrefs, setCustomerPrefs] = useState({
    cloud_strategy: 'neutral',
    regulatory_constraints: false,
    budget_envelope: 'medium',
    idp_preference: 'Azure AD / Entra ID',
    compliance_frameworks: ['SOC 2', 'ISO 27001']
  });
  const [showPrefsModal, setShowPrefsModal] = useState(false);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    fetchLatest();
  }, [project.id]);

  useEffect(() => {
    if (recommendation && activeTab === 'topology') {
      buildTopologyDiagram();
    }
  }, [recommendation, activeTab]);

  const fetchLatest = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/architecture/latest`);
      setRecommendation(response.data.recommendation);
    } catch (error) {
      console.log('No architecture recommendation yet');
    } finally {
      setLoading(false);
    }
  };

  const generateRecommendation = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/architecture/generate`, 
        { customer_preferences: customerPrefs }, 
        
      );
      setRecommendation(response.data);
      setShowPrefsModal(false);
    } catch (error) {
      console.error('Error:', error);
      alert('Error generating recommendation. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const buildTopologyDiagram = () => {
    if (!recommendation) return;

    const systems = recommendation.landscape_topology?.systems || [];
    const newNodes = [];
    const newEdges = [];

    systems.forEach((system, index) => {
      const isProd = system.tier === 'prod';
      const color = isProd ? '#10B981' : '#3B82F6';
      
      newNodes.push({
        id: system.id,
        type: 'default',
        position: { x: index * 250 + 50, y: isProd ? 100 : 300 },
        data: { 
          label: (
            <div className="text-center">
              <div className="font-bold text-sm">{system.name}</div>
              <div className="text-xs text-gray-500">{system.type}</div>
            </div>
          )
        },
        style: {
          background: color,
          color: 'white',
          border: '2px solid white',
          borderRadius: '8px',
          padding: '10px',
          width: 180
        }
      });
    });

    for (let i = 0; i < systems.length - 1; i++) {
      if (systems[i].tier === systems[i + 1].tier) {
        newEdges.push({
          id: `e${systems[i].id}-${systems[i + 1].id}`,
          source: systems[i].id,
          target: systems[i + 1].id,
          type: 'smoothstep',
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed }
        });
      }
    }

    setNodes(newNodes);
    setEdges(newEdges);
  };

  const downloadCIOSummary = () => {
    if (!recommendation?.ai_narratives?.cio_summary) return;
    
    const content = `# Architecture Recommendation - CIO Summary\n\nProject: ${project.name}\nGenerated: ${new Date().toLocaleString()}\n\n## Executive Summary\n\n${recommendation.ai_narratives.cio_summary}\n\n## Recommended Deployment Model\n\n${recommendation.deployment_evaluation.recommended.archetype.name}\n\nScore: ${recommendation.deployment_evaluation.recommended.total_score}/100\n\n## Key Metrics\n\n- Total Systems: ${recommendation.landscape_topology.system_count}\n- Production HANA Memory: ${recommendation.sizing_estimates.environments.production.hana_memory_gb}GB\n- Estimated Annual Compute Cost: $${recommendation.sizing_estimates.total_annual_compute_cost_usd.toLocaleString()}\n`;
    
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AURA_Architecture_CIO_Summary_${project.name.replace(/\s/g, '_')}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
    </div>
  );

  const DeploymentCard = ({ option, isRecommended }) => (
    <div className={`border-2 rounded-xl p-6 ${
      isRecommended 
        ? 'border-green-500 bg-green-50' 
        : 'border-gray-200 bg-white'
    }`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          {option.archetype.id === 'rise_with_sap' && <Cloud className="w-8 h-8 text-blue-600" />}
          {option.archetype.id === 'hyperscaler_hosted' && <Server className="w-8 h-8 text-purple-600" />}
          {option.archetype.id === 'on_premise' && <Building2 className="w-8 h-8 text-gray-600" />}
          <div>
            <h4 className="text-lg font-bold text-gray-900">{option.archetype.name}</h4>
            <p className="text-sm text-gray-600">{option.archetype.model_type}</p>
          </div>
        </div>
        {isRecommended && (
          <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
            RECOMMENDED
          </span>
        )}
      </div>

      <p className="text-sm text-gray-700 mb-4">{option.archetype.description}</p>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Total Score:</span>
          <span className="font-bold text-gray-900">{option.total_score}/100</span>
        </div>
        {Object.entries(option.scores).map(([key, value]) => (
          <div key={key} className="flex items-center space-x-2">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-500" 
                style={{ width: `${value}%` }}
              ></div>
            </div>
            <span className="text-xs text-gray-600 w-24">{key.replace(/_/g, ' ')}</span>
            <span className="text-xs font-semibold text-gray-900 w-8">{value}</span>
          </div>
        ))}
      </div>

      <div className="border-t pt-4">
        <p className="text-xs text-gray-600 mb-2">Best For:</p>
        <div className="flex flex-wrap gap-2">
          {option.archetype.best_for.map((item, idx) => (
            <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Architecture & Deployment Advisor</h2>
            <p className="text-gray-600">AI-powered S/4HANA target architecture recommendation</p>
            
            {/* SAP System Indicator */}
            {recommendation && recommendation.sap_system_analyzed && recommendation.sap_system_analyzed.environment_name && (
              <div className="mt-3 flex items-center space-x-3 text-sm">
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-700">Analyzing:</span>
                  <span className="font-bold text-blue-600">{recommendation.sap_system_analyzed.environment_name}</span>
                  <span className="text-gray-500">({recommendation.sap_system_analyzed.host})</span>
                </div>
                {recommendation.data_source === 'REAL_SAP_SYSTEM' && (
                  <div className="px-3 py-1.5 bg-green-100 border border-green-300 rounded-lg">
                    <span className="text-green-800 font-semibold flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>📊 Real Data</span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {recommendation && (
              <button 
                onClick={downloadCIOSummary}
                className="bg-green-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2"
              >
                <Download className="w-5 h-5" />
                <span>CIO Summary</span>
              </button>
            )}
            <button 
              onClick={() => setShowPrefsModal(true)}
              disabled={generating}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  <span>Generate Architecture</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Preferences Modal */}
      {showPrefsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-2xl font-bold mb-6">Customer Preferences</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Cloud Strategy</label>
                <select 
                  value={customerPrefs.cloud_strategy}
                  onChange={(e) => setCustomerPrefs({...customerPrefs, cloud_strategy: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2"
                >
                  <option value="cloud_first">Cloud First</option>
                  <option value="neutral">Neutral</option>
                  <option value="on_premise_preferred">On-Premise Preferred</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Budget Envelope</label>
                <select 
                  value={customerPrefs.budget_envelope}
                  onChange={(e) => setCustomerPrefs({...customerPrefs, budget_envelope: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <input 
                  type="checkbox"
                  checked={customerPrefs.regulatory_constraints}
                  onChange={(e) => setCustomerPrefs({...customerPrefs, regulatory_constraints: e.target.checked})}
                  className="w-4 h-4"
                />
                <label className="text-sm font-semibold">Strict Regulatory Constraints</label>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Identity Provider Preference</label>
                <input 
                  type="text"
                  value={customerPrefs.idp_preference}
                  onChange={(e) => setCustomerPrefs({...customerPrefs, idp_preference: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-6">
              <button 
                onClick={generateRecommendation}
                disabled={generating}
                className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 disabled:opacity-50"
              >
                {generating ? 'Generating...' : 'Generate'}
              </button>
              <button 
                onClick={() => setShowPrefsModal(false)}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!recommendation && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Building2 className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Architecture Recommendation</h3>
          <p className="text-gray-600 mb-6">Generate a tailored S/4HANA architecture based on Phase 1 findings</p>
          <button 
            onClick={() => setShowPrefsModal(true)}
            className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 inline-flex items-center space-x-2"
          >
            <Play className="w-5 h-5" />
            <span>Generate Architecture</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      {recommendation && (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <Cloud className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{recommendation.deployment_evaluation.recommended.total_score}</div>
              <div className="text-blue-100 text-sm">Confidence Score</div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <Database className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{recommendation.sizing_estimates.environments.production.hana_memory_gb}GB</div>
              <div className="text-green-100 text-sm">HANA Memory</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <Server className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{recommendation.landscape_topology.system_count}</div>
              <div className="text-purple-100 text-sm">Systems</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <DollarSign className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">${(recommendation.sizing_estimates.total_annual_compute_cost_usd / 1000).toFixed(0)}K</div>
              <div className="text-orange-100 text-sm">Annual Cost</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: Building2 },
                { id: 'deployment', label: 'Deployment Models', icon: Cloud },
                { id: 'topology', label: 'Landscape', icon: Network },
                { id: 'sizing', label: 'Sizing', icon: Database },
                { id: 'narratives', label: 'AI Summary', icon: Sparkles }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-6 py-3 font-medium transition-all flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center space-x-2">
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                      <span>Recommended Architecture</span>
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Deployment Model</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {recommendation.deployment_evaluation.recommended.archetype.name}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Model Type</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {recommendation.deployment_evaluation.recommended.archetype.model_type}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        <span>Key Features</span>
                      </h4>
                      <ul className="space-y-2">
                        {recommendation.deployment_evaluation.recommended.archetype.key_features.map((feature, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start space-x-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span>Best For</span>
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {recommendation.deployment_evaluation.recommended.archetype.best_for.map((item, idx) => (
                          <span key={idx} className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <AlertTriangle className="w-5 h-5 text-orange-600" />
                        <span>Considerations</span>
                      </h4>
                      <ul className="space-y-2">
                        {recommendation.deployment_evaluation.recommended.archetype.constraints.map((constraint, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start space-x-2">
                            <span className="text-orange-600">•</span>
                            <span>{constraint}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Deployment Models Tab */}
              {activeTab === 'deployment' && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {recommendation.deployment_evaluation.options.map((option) => (
                      <DeploymentCard 
                        key={option.archetype.id}
                        option={option}
                        isRecommended={option.recommendation_level === 'Recommended'}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Topology Tab */}
              {activeTab === 'topology' && (
                <div className="space-y-6">
                  <div style={{ height: '500px' }} className="border-2 border-gray-200 rounded-xl">
                    <ReactFlow
                      nodes={nodes}
                      edges={edges}
                      onNodesChange={onNodesChange}
                      onEdgesChange={onEdgesChange}
                      fitView
                    >
                      <Controls />
                      <MiniMap />
                      <Background variant="dots" gap={12} size={1} />
                    </ReactFlow>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Systems ({recommendation.landscape_topology.system_count})</h4>
                      <div className="space-y-3">
                        {recommendation.landscape_topology.systems.map((system) => (
                          <div key={system.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                            <Server className="w-5 h-5 text-blue-600 mt-1" />
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{system.name}</p>
                              <p className="text-sm text-gray-600">{system.type} • {system.purpose}</p>
                              <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                system.tier === 'prod' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {system.tier.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">HA/DR Strategy</h4>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">High Availability</p>
                          <p className="font-semibold text-gray-900">{recommendation.landscape_topology.ha_dr_strategy.high_availability.approach}</p>
                          <p className="text-xs text-gray-500">RPO: {recommendation.landscape_topology.ha_dr_strategy.high_availability.rpo_minutes} min | RTO: {recommendation.landscape_topology.ha_dr_strategy.high_availability.rto_minutes} min</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Disaster Recovery</p>
                          <p className="font-semibold text-gray-900">{recommendation.landscape_topology.ha_dr_strategy.disaster_recovery.approach}</p>
                          <p className="text-xs text-gray-500">RPO: {recommendation.landscape_topology.ha_dr_strategy.disaster_recovery.rpo_minutes} min | RTO: {recommendation.landscape_topology.ha_dr_strategy.disaster_recovery.rto_hours} hrs</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Backup Strategy</p>
                          <p className="font-semibold text-gray-900">{recommendation.landscape_topology.ha_dr_strategy.backup_strategy.frequency}</p>
                          <p className="text-xs text-gray-500">Retention: {recommendation.landscape_topology.ha_dr_strategy.backup_strategy.retention_days} days</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sizing Tab */}
              {activeTab === 'sizing' && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {Object.entries(recommendation.sizing_estimates.environments).map(([env, sizing]) => (
                      <div key={env} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                        <h4 className="font-bold text-gray-900 mb-4 capitalize">{env.replace(/_/g, '-')}</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">HANA Memory:</span>
                            <span className="font-bold">{sizing.hana_memory_gb} GB</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">App CPU Cores:</span>
                            <span className="font-bold">{sizing.app_cpu_cores}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">App Memory:</span>
                            <span className="font-bold">{sizing.app_memory_gb} GB</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Disk Size:</span>
                            <span className="font-bold">{sizing.disk_size_gb} GB</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Disk IOPS:</span>
                            <span className="font-bold">{sizing.disk_iops.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {recommendation.sizing_estimates.cloud_instances && (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Recommended Cloud Instances (Production)</h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">AWS</p>
                          <p className="font-semibold text-gray-900">{recommendation.sizing_estimates.cloud_instances.production.aws}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Azure</p>
                          <p className="font-semibold text-gray-900">{recommendation.sizing_estimates.cloud_instances.production.azure}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">GCP</p>
                          <p className="font-semibold text-gray-900">{recommendation.sizing_estimates.cloud_instances.production.gcp}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-2">Estimated Annual Compute Cost</h4>
                    <p className="text-4xl font-bold text-green-600">${recommendation.sizing_estimates.total_annual_compute_cost_usd.toLocaleString()}</p>
                    <p className="text-sm text-gray-600 mt-2">Based on list pricing. Actual costs may vary with commitments and discounts.</p>
                  </div>
                </div>
              )}

              {/* AI Narratives Tab */}
              {activeTab === 'narratives' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold flex items-center space-x-2">
                        <FileText className="w-6 h-6 text-blue-600" />
                        <span>CIO Executive Summary</span>
                      </h3>
                      {recommendation.ai_narratives.generated_by.includes('GPT') && (
                        <span className="px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-full flex items-center space-x-1">
                          <Sparkles className="w-3 h-3" />
                          <span>{recommendation.ai_narratives.generated_by}</span>
                        </span>
                      )}
                    </div>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 whitespace-pre-wrap">{recommendation.ai_narratives.cio_summary}</p>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center space-x-2">
                      <Building2 className="w-6 h-6 text-gray-600" />
                      <span>Solution Architect Rationale</span>
                    </h3>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 whitespace-pre-wrap">{recommendation.ai_narratives.architect_rationale}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ArchitectureAdvisor;
